import { useMemo } from "react"

import { AnimatedSkeletonList } from "@/components/common/AnimatedSkeletonList/AnimatedSkeletonList"
import { SplitViewQuestionHeader } from "@/components/common/SplitView/SplitViewQuestionHeader"
import { Typography } from "@/components/common/Typography/Typography"
import { PdfAnswerItem } from "@/components/feature/CollapsibleQuestionnaireList/PdfAnswerItem/PdfAnswerItem"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQuestionListFilters } from "@/pages/questionnaire/useQuestionListFilters"
import { Questionnaire } from "@/types/questionnaire/models"

/** Strip "sort_id: " prefix so "1: Telefon" → "Telefon" for duplicate detection. */
const stripSortPrefix = (q: string) => q.replace(/^\d+[\d.]*:\s*/, "")

/**
 * Generated fallback section names ("Section_1", "Questionnaire", "General") are
 * bookkeeping, not headings — rendering them would add noise, not orientation.
 */
const isPlaceholderSectionName = (name: string | undefined) =>
  !name || /^(section[_ ]?\d*|questionnaire|general)$/i.test(name.trim())

interface PdfQASectionProps {
  questionnaire: Questionnaire | undefined
  isLoading: boolean
  answeredQuestionCount: number
  numTotalQuestions: number
  answeredCountBySection?: Record<string, number>
  totalCountBySection?: Record<string, number>
  handleTextChange: (responsePairId: string, nextValue: string) => void
  handleMultipleChoiceChange: (
    responsePairId: string,
    nextValue: string,
  ) => void
  handleMultipleSelectChange: (
    responsePairId: string,
    nextValue: string,
  ) => void
  selectedQuestionId: string | undefined
  onInputFocus: (questionId: string, optionBoxId?: string) => void
  onDeleteResponsePair?: (responsePairId: string) => void
}

/**
 * Component that displays the Q&A section with answers list.
 * Handles loading state and displays questionnaire sections.
 */
export const PdfQASection: React.FC<PdfQASectionProps> = ({
  questionnaire,
  isLoading,
  answeredQuestionCount,
  numTotalQuestions,
  handleTextChange,
  handleMultipleChoiceChange,
  handleMultipleSelectChange,
  selectedQuestionId,
  onInputFocus,
  onDeleteResponsePair,
}) => {
  const { translate } = useTranslation()
  // Follow the questionnaire's stored extraction stamp, not the viewer's flag, so section
  // headers show for an Extend questionnaire regardless of who is viewing it.
  const useExtendEnabled = questionnaire?.usesExtend ?? false
  const { activeFilter, filterCounts, setActiveFilter, visibleSections } =
    useQuestionListFilters(questionnaire)

  const duplicateNames = useMemo(() => {
    if (!questionnaire) return new Set<string>()
    const counts = new Map<string, number>()
    for (const section of questionnaire.sections) {
      for (const pair of section.responsePairs) {
        const name = stripSortPrefix(pair.question)
        counts.set(name, (counts.get(name) ?? 0) + 1)
      }
    }
    const dupes = new Set<string>()
    for (const [name, count] of counts) {
      if (count > 1) dupes.add(name)
    }
    return dupes
  }, [questionnaire])

  if (isLoading) {
    return (
      <>
        <Typography as="h2" variant="titleM" className="pb-L">
          {translate("QUESTIONNAIRE_ANSWERS/PAGE_TITLE")}
        </Typography>
        <AnimatedSkeletonList />
      </>
    )
  }

  if (!questionnaire) {
    return null
  }

  return (
    <div className="flex h-full flex-col">
      <SplitViewQuestionHeader
        countLabelKey="QUESTIONNAIRE_ANSWERS/COUNT_TOTAL_ANSWERS"
        activeFilter={activeFilter}
        filterCounts={filterCounts}
        setActiveFilter={setActiveFilter}
        countValueDisplay={
          <>
            <Typography
              as="span"
              variant="textM"
              className="font-bold text-yellow1"
            >
              {answeredQuestionCount}
            </Typography>
            <Typography
              as="span"
              variant="textM"
              className="font-bold text-white"
            >
              /{numTotalQuestions}
            </Typography>
          </>
        }
      />

      <div className="flex-1 overflow-y-auto pr-4 pt-XS">
        <div className="flex flex-col gap-8">
          {visibleSections.map((section, index) => (
            <div
              key={`pdf-${section.id}-${index}`}
              className="flex flex-col gap-6"
            >
              {useExtendEnabled && !isPlaceholderSectionName(section.name) && (
                <Typography
                  as="h3"
                  variant="textM-bold"
                  className="border-b border-grey7 pb-2"
                >
                  {section.name}
                </Typography>
              )}
              {section.visibleResponsePairs.map((pair, pairIndex) => (
                <PdfAnswerItem
                  key={`${pair.id}-${pairIndex}`}
                  responsePair={pair}
                  value={pair.response || ""}
                  onChange={(nextValue) => {
                    if (pair.type === "free-text") {
                      handleTextChange(pair.id, nextValue)
                    } else if (
                      pair.type === "boolean" ||
                      pair.type === "multiple-choice"
                    ) {
                      handleMultipleChoiceChange(pair.id, nextValue)
                    } else if (pair.type === "multiple-select") {
                      handleMultipleSelectChange(pair.id, nextValue)
                    }
                  }}
                  selectedQuestionId={selectedQuestionId}
                  onInputFocus={onInputFocus}
                  onDelete={onDeleteResponsePair}
                  questionnaireId={questionnaire?.id ?? ""}
                  showContext={duplicateNames.has(
                    stripSortPrefix(pair.question),
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
